import Banner from '@rio-cloud/rio-uikit/Banner';
import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { type ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router';

import { extractLanguage } from '@/configuration/lang/lang';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { userManualFaq } from '@/features/userManual/utils';
import { formatUrl, t } from '@/lib/utils';

const formattedMessageValues = {
    b: (content: ReactNode) => <b>{content}</b>,
    i: (content: ReactNode) => <i>{content}</i>,
    ul: (content: ReactNode) => (
        <ul style={{ listStyleType: 'initial' }} className='margin-left-15 margin-top-0'>
            {content}
        </ul>
    ),
    ol: (content: ReactNode) => <ol className='margin-left-15 margin-top-0 list-decimal'>{content}</ol>,
    li: (content: ReactNode) => <li>{content}</li>,
    mailto: (content: ReactNode) => <Link to={`mailto:${content}`}>{content}</Link>,
    linebreak: <br />,
};
const faqPrefix = 'userManual.faq';

const ContactSupportPanel = () => (
    <div>
        <Banner
            className='bg-highlight-decent border-color-primary'
            border={true}
            show={true}
            dismissible={false}
            initialAnimation={false}
        >
            <Banner.Page>
                <Banner.Content
                    title={t(`${faqPrefix}.support.title`)}
                    actions={
                        <Banner.Actions>
                            <Button
                                bsStyle={Button.PRIMARY}
                                onClick={() => (window.location.href = 'mailto:man-charge-go@man.eu')}
                            >
                                <span className='uikit-icon icon-envelope text-bold text-size-14' />
                                <span className='padding-left-5'>
                                    <FormattedMessage id={`${faqPrefix}.support.button`} />
                                </span>
                            </Button>
                        </Banner.Actions>
                    }
                >
                    <FormattedMessage id={`${faqPrefix}.support.description`} values={{ b: chunk => <b>{chunk}</b> }} />
                </Banner.Content>
            </Banner.Page>
        </Banner>
    </div>
);

const FaqPageDialogContent = () => {
    const location = useLocation();
    const intl = useIntl();
    const userLanguage = extractLanguage(intl.locale);

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [location.hash]);

    const triggerGAEvent = (label: string) => {
        const question = label.replace('.question', '').replace(/\./g, '_');

        trackCustomEvent(
            `open_${question}`,
            {
                trigger: 'click',
                flow_name: 'user_manual_faq',
                element_name: 'click_on_expander',
            },
            createDebugConfig()
        );
    };

    return (
        <>
            <h4 className='text-normal text-transform-none margin-top-20 margin-bottom-25'>
                <FormattedMessage id={`${faqPrefix}.title`} />
            </h4>
            {userManualFaq.map(faqSection => (
                <div id={faqSection.keyFaqPage} key={faqSection.keyFaqPage} className='margin-bottom-50'>
                    <h5 className='text-normal margin-bottom-20'>
                        <FormattedMessage id={`${faqPrefix}.${faqSection.keyFaqPage}.title`} />
                    </h5>
                    {faqSection.listKeys.map(faq => (
                        <ExpanderPanel
                            key={faq.question}
                            title={t(`${faqPrefix}.${faq.question}`)}
                            className='border-color-light margin-bottom-10'
                            onEntered={() => triggerGAEvent(faq.question)}
                        >
                            <FormattedMessage
                                id={`${faqPrefix}.${faq.description}`}
                                values={{
                                    ...formattedMessageValues,
                                    link: content =>
                                        faq.link ? (
                                            <a
                                                className='text-color-primary'
                                                href={formatUrl(faq.link, userLanguage)}
                                                target='_blank'
                                                rel='noreferrer'
                                            >
                                                {content}
                                            </a>
                                        ) : (
                                            content
                                        ),
                                }}
                            />
                        </ExpanderPanel>
                    ))}
                </div>
            ))}
            <ContactSupportPanel />
        </>
    );
};

export default FaqPageDialogContent;
